import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

/**
 * Only `primary` uses the accent fill, and a screen should rarely have more
 * than one. Everything else is a surface step or nothing at all, which is
 * what keeps the accent meaningful.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover active:bg-accent-active shadow-xs',
  secondary:
    'bg-surface-raised text-fg border border-border hover:bg-surface-overlay hover:border-border-strong',
  ghost: 'text-fg-muted hover:bg-surface-raised hover:text-fg',
  danger: 'bg-lost-subtle text-lost border border-transparent hover:border-lost/30',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-7 gap-1.5 px-2.5 text-xs rounded-sm',
  md: 'h-8 gap-2 px-3 text-sm rounded-md',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders before the label. Pass an icon at its default 16px. */
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Shows a spinner in place of `leading` and blocks interaction. */
  loading?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  leading,
  trailing,
  loading = false,
  fullWidth = false,
  disabled,
  type = 'button',
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANT[variant],
        SIZE[size],
        fullWidth && 'w-full',
      )}
      {...props}
    >
      {loading ? <Spinner /> : leading}
      {children}
      {trailing}
    </button>
  );
}

/**
 * The one place a spinner is allowed. Waiting for a page uses skeletons;
 * waiting for an action you just triggered needs feedback on the control.
 */
function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        className="origin-center animate-spin"
      />
    </svg>
  );
}
